import { useDownloadPublicPaymentReceiptPdfMutation } from '@massilia/queries'
import { useMemo } from 'react'

import type { PurchasePayload } from '@/lib/gtm'

import { PaymentReceiptActions } from '@/components/payment-confirmation/payment-receipt-actions'
import { useTrackPurchaseOnce } from '@/hooks/use-track-purchase-once'

import { SimplePaymentConfirmation } from './simple-payment-confirmation'

const INSURANCE_STRINGS = {
  failedDescription:
    "Le paiement de votre assurance n'a pas pu être traité. Veuillez réessayer depuis votre espace client ou contacter notre équipe si besoin.",
  failedTitle: "Paiement de l'assurance échoué",
  successDescriptionBuilder: (amountLabel?: string, referenceLabel?: string) =>
    `Votre paiement d'assurance${amountLabel ? ` de ${amountLabel}` : ''}${referenceLabel ?? ''} a bien été enregistré. Vous recevrez une confirmation par email sous peu.`,
  successTitle: 'Assurance confirmée !',
  unknownDescription:
    "Nous n'avons pas pu confirmer l'état de votre paiement d'assurance. Vérifiez votre espace client ou contactez notre équipe si besoin.",
  unknownTitle: 'Statut de paiement indisponible',
} as const

interface InsurancePaymentConfirmationProps {
  transId?: string
  amountCents?: number
  reference?: string
  status: 'failed' | 'success' | 'unknown'
}

export function InsurancePaymentConfirmation({
  transId,
  amountCents,
  reference,
  status,
}: InsurancePaymentConfirmationProps) {
  const downloadReceiptMutation = useDownloadPublicPaymentReceiptPdfMutation()

  // Conversion Google Ads pour le paiement d'assurance seule. Le montant est
  // encodé dans le UserData Axepta (`insurance|...|<amountCents>|...`) puis
  // transmis ici via `amountCents` par confirmation-paiement.$status.tsx.
  const purchasePayload = useMemo<null | PurchasePayload>(() => {
    if (status !== 'success' || !reference || amountCents == null) return null
    const value = amountCents / 100
    return {
      transactionId: `${reference}-assurance`,
      currency: 'EUR',
      items: [{ item_name: `Assurance réservation ${reference}`, price: value, quantity: 1 }],
      value,
    }
  }, [status, reference, amountCents])
  useTrackPurchaseOnce(purchasePayload)

  const handleDownloadReceipt = async () => {
    if (!transId) {
      throw new Error('missing transId')
    }
    return downloadReceiptMutation.mutateAsync(transId)
  }

  const extraActions = transId ? (
    <PaymentReceiptActions
      isLoading={downloadReceiptMutation.isPending}
      onDownload={handleDownloadReceipt}
    />
  ) : undefined

  return (
    <SimplePaymentConfirmation
      amountCents={amountCents}
      extraActions={extraActions}
      reference={reference}
      status={status}
      strings={INSURANCE_STRINGS}
    />
  )
}
